import type { StyleSpecification } from 'maplibre-gl'
import type { MapTheme } from './types'

/**
 * Стили VersaTiles (контракт §10) — копии tiles.versatiles.org/assets/styles/<имя>/style.json в styles/.
 * Подложку (тайлы, шрифты, значки) отдаёт наш сервер по /map/* с той же раскладкой путей (ADR 0019):
 * сервер VersaTiles один и в Германии — из Вьетнама загрузка карты занимала 3–4 с.
 */
const STYLE_LOADERS: Record<MapTheme, () => Promise<{ default: unknown }>> = {
  dark: () => import('./styles/colorful-dark.json'),
  light: () => import('./styles/colorful.json'),
}

/** Откуда стили VersaTiles берут подложку. */
export const VERSATILES_ORIGIN = 'https://tiles.versatiles.org'

/** Где подложку отдаём мы (Caddy; в dev — прокси Vite на VersaTiles). */
export const MAP_ASSETS_PATH = '/map'

/** Адреса VersaTiles → наш сервер; проекция globe и небо не нужны карте города. */
export function localizeStyle(style: StyleSpecification, base: string): StyleSpecification {
  const swap = (u: string) => (u.startsWith(VERSATILES_ORIGIN) ? base + u.slice(VERSATILES_ORIGIN.length) : u)
  const sources: StyleSpecification['sources'] = {}
  for (const [id, src] of Object.entries(style.sources)) {
    sources[id] = 'tiles' in src && src.tiles ? { ...src, tiles: src.tiles.map(swap) } : src
  }
  const { projection: _p, sky: _s, ...rest } = style
  return {
    ...rest,
    sources,
    glyphs: style.glyphs && swap(style.glyphs),
    sprite: Array.isArray(style.sprite) ? style.sprite.map((s) => ({ ...s, url: swap(s.url) })) : style.sprite && swap(style.sprite),
  }
}

const cache = new Map<MapTheme, Promise<StyleSpecification>>()

/** Стиль темы с адресами нашего сервера; грузится отдельным чанком параллельно с MapLibre. */
export function loadMapStyle(theme: MapTheme): Promise<StyleSpecification> {
  let p = cache.get(theme)
  if (!p) {
    const base = (typeof location !== 'undefined' ? location.origin : '') + MAP_ASSETS_PATH
    p = STYLE_LOADERS[theme]().then((m) => localizeStyle(m.default as StyleSpecification, base))
    p.catch(() => cache.delete(theme))
    cache.set(theme, p)
  }
  return p
}

/** Центр Далата — если точек нет. */
export const DALAT_CENTER = { lat: 11.9404, lng: 108.4583 }

/** Цвет маршрута, если CSS-переменная --pri недоступна (значения токенов макета). */
export const PRI_FALLBACK: Record<MapTheme, string> = { dark: '#EC7A2C', light: '#E06A16' }

/** Сколько ждать загрузки стиля, прежде чем показать фолбэк-карту. */
export const LOAD_TIMEOUT_MS = 15000

/** Подложка линии маршрута — контраст с дорогами стиля (тёмная тема: почти чёрная, светлая: белая). */
export const ROUTE_CASING: Record<MapTheme, string> = { dark: '#111214', light: '#FFFFFF' }
/** Цвет троп, если токен --cash не прочитался (макет Map View: жёлтый пунктир пути). */
export const TRAIL_FALLBACK: Record<MapTheme, string> = { dark: '#F9CB30', light: '#C99A0A' }
